import type { AuthUser } from '../models/v2/auth';

const STORAGE_KEYS = {
    SESSION_ID: 'auth_session_id',
    EXPIRES_AT: 'auth_expires_at',
    USER: 'auth_user',
};

/**
 * AuthStore
 * 
 * Single source of truth for client-side session management.
 * Handles persistence to localStorage and proactive expiry checks.
 */
export const AuthStore = {
    /**
     * Store a new session
     */
    setSession(sessionId: string, expiresAt: string, user: AuthUser) {
        localStorage.setItem(STORAGE_KEYS.SESSION_ID, sessionId);
        localStorage.setItem(STORAGE_KEYS.EXPIRES_AT, expiresAt);
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
    },

    /**
     * Clear current session (logout)
     */
    clearSession() {
        localStorage.removeItem(STORAGE_KEYS.SESSION_ID);
        localStorage.removeItem(STORAGE_KEYS.EXPIRES_AT);
        localStorage.removeItem(STORAGE_KEYS.USER);
    },

    /**
     * Get current session ID
     */
    getSessionId(): string | null {
        return localStorage.getItem(STORAGE_KEYS.SESSION_ID);
    },

    /**
     * Get current user
     */
    getUser(): AuthUser | null {
        const userStr = localStorage.getItem(STORAGE_KEYS.USER);
        if (!userStr) return null;
        try {
            return JSON.parse(userStr);
        } catch (e) {
            console.error('Failed to parse user from storage', e);
            return null;
        }
    },

    /**
     * Check if session is expired (proactive check)
     * Returns true if expired or no expiry date set
     */
    isExpired(): boolean {
        const expiresAt = localStorage.getItem(STORAGE_KEYS.EXPIRES_AT);
        if (!expiresAt) return true; // Treat missing expiry as expired if session exists? Actually if no session, isExpired doesn't matter much but let's be safe.

        // If no session ID, we can't be "expired" in the sense of needing to logout, but we are definitely not valid.
        // However, this method specifically checks the TIME.
        return Date.now() > new Date(expiresAt).getTime();
    },

    /**
     * Check if user is authenticated (valid session + not expired)
     */
    isAuthenticated(): boolean {
        const sessionId = this.getSessionId();
        if (!sessionId) return false;
        return !this.isExpired();
    }
};
